import { injectable } from 'inversify';
import { action, autorun, IReactionDisposer, makeObservable, when } from 'mobx';
import { INotificationsService } from '../notifications/services/notifications.service';
import { IImage } from './annotations.interface';
import { IAnnotationsStore } from './annotations.store';
import { IAnnotationUiStore } from './annotationUi.store';
import { IImagesQueueService } from './imagesQueueServiceBase';

export interface IBaseAnnotationControl {
  setupAsync(projectId: string, imagesQueueService: IImagesQueueService): Promise<void>;
  displayImageFromQueue(): void;
  dispose(): void;
  uiStore: IAnnotationUiStore;
  imagesQueueService: IImagesQueueService;
  notificationsService: INotificationsService;
  annotationsStore: IAnnotationsStore;
}

@injectable()
export abstract class BaseAnnotationControl implements IBaseAnnotationControl {
  uiStore: IAnnotationUiStore;
  imagesQueueService!: IImagesQueueService;
  notificationsService: INotificationsService;
  annotationsStore: IAnnotationsStore;

  private disposeNoMoreImagesAutorun?: IReactionDisposer;
  private disposeNoMoreImagesReaction?: IReactionDisposer;
  private disposeImageLoadedReaction?: IReactionDisposer;

  constructor(uiStore: IAnnotationUiStore, notificationsService: INotificationsService, annotationsStore: IAnnotationsStore) {
    makeObservable<BaseAnnotationControl, 'handleUndefinedImage'>(this, {
      handleUndefinedImage: action,
      resetZoom: action.bound,
      assignNoImageReason: action,
    });

    this.uiStore = uiStore;
    this.notificationsService = notificationsService;
    this.annotationsStore = annotationsStore;
  }

  abstract setupAsync(projectId: string, imagesQueueService: IImagesQueueService): Promise<void>;

  abstract handleImageDisplayed: (image: IImage) => void;

  protected initReactions(): void {
    this.dispose();
    this.disposeNoMoreImagesAutorun = autorun(() => {
      this.uiStore.changeIsImageLoading(this.imagesQueueService.areAnyImagesToLoad && this.annotationsStore.image === undefined);
    });
  }

  displayImageFromQueue(): void {
    this.annotationsStore.annotationStatus = undefined;
    this.uiStore.changeIsImageLoading(true);
    this.annotationsStore.setImageName('', '', 'None');
    setTimeout(() => {
      this.displayImage();
    }, 0);
  }

  displayCurrentImage = (): void => {
    this.annotationsStore.annotationStatus = undefined;
    this.uiStore.changeIsImageLoading(false);
    const image = this.annotationsStore.image;
    if (this.handleImageDisplayed && image) {
      this.annotationsStore.setImageName(image.name, image.dataSetName, image.datasetStatus);
      this.handleImageDisplayed(image);
    }
  };

  displayImage = (): void => {
    this.annotationsStore.annotationStatus = undefined;
    this.annotationsStore.image = undefined;
    const image = this.imagesQueueService.takeImageFromQueue();
    if (!image) {
      this.handleUndefinedImage();
    }
    this.annotationsStore.image = image;
    if (this.handleImageDisplayed && image) {
      this.annotationsStore.setImageName(image.name, image.dataSetName, image.datasetStatus);
      this.handleImageDisplayed(image);
    }
  };

  protected handleUndefinedImage(): void {
    this.annotationsStore.annotationStatus = undefined;
    this.annotationsStore.image = undefined;
    this.annotationsStore.setImageName('', '', 'None');

    if (!this.imagesQueueService.areAnyImagesToLoad) {
      this.uiStore.changeIsImageLoading(false);
      this.disposeNoMoreImagesReaction = when(
        () => !this.imagesQueueService.isAnythingToDisplay && !this.imagesQueueService.areAnyImagesToLoad,
        () => {
          this.resetZoom();
          this.assignNoImageReason();
        }
      );
    } else {
      this.disposeNoMoreImagesReaction = when(
        () => !this.imagesQueueService.areAnyImagesToLoad,
        () => {
          this.resetZoom();
          this.assignNoImageReason();
        }
      );
    }

    this.disposeImageLoadedReaction = when(
      () => this.imagesQueueService.isAnythingToDisplay,
      () => {
        this.disposeNoMoreImagesReaction?.();
        this.displayImageFromQueue();
      }
    );
  }

  resetZoom(): void {
    this.uiStore.changeZoomLevel(100);
  }

  assignNoImageReason(): void {
    this.annotationsStore.noImageReason = this.imagesQueueService.noImageReason;
  }

  dispose(): void {
    this.disposeNoMoreImagesAutorun?.();
    this.disposeImageLoadedReaction?.();
    this.disposeNoMoreImagesReaction?.();
  }
}
